// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/components/admin/MemberHistoryButton.js
//
// Frise "parcours du membre" PAR UN ADMIN — 2026-09-16. Rendu uniquement là
// où `isAdmin === true` (même convention que AdminEditProfileButton, monté
// juste à côté dans MemberDetailsCard). Le backend re-vérifie de toute façon
// (checkRole admin) : 403 sinon.
//
// Lecture seule — contrairement à AdminEditProfileButton, ce composant ne
// modifie rien, pas de callback onUpdate nécessaire.
//
// Source des événements : GET /members/:id/history (agrégation backend de
// member_attribute_history + donations + member_contributions +
// event_registrations + member_audit_logs, déjà normalisée en
// {type, date, detail} — voir routes/member.js). Le formatage
// {iconKey, title, detail} affichable est une fonction PURE partagée
// (utils/memberHistoryFormat.js), testée indépendamment du rendu.
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CAlert,
  CSpinner,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilHistory,
  cilFlagAlt,
  cilStar,
  cilBriefcase,
  cilLockLocked,
  cilCash,
  cilCalendarCheck,
  cilPencil,
} from '@coreui/icons'
import { get } from '../../utils/apiClient'
import { formatDateI18n } from '../../utils/i18nFormatters'
import { formatHistoryEvent } from '../../utils/memberHistoryFormat'

const PAGE_SIZE = 50

const ICONS = {
  flag: cilFlagAlt,
  star: cilStar,
  briefcase: cilBriefcase,
  lock: cilLockLocked,
  cash: cilCash,
  calendar: cilCalendarCheck,
  pencil: cilPencil,
}

const MemberHistoryButton = ({ member, renderTrigger }) => {
  const { t, i18n } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [events, setEvents] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)

  const fetchPage = (offset) =>
    get(`/members/${member.id}/history?limit=${PAGE_SIZE}&offset=${offset}`)

  const openModal = () => {
    setShowModal(true)
    setLoading(true)
    setError(null)
    fetchPage(0)
      .then((res) => {
        setEvents(res?.events || [])
        setHasMore(Boolean(res?.hasMore))
      })
      .catch(() => setError(t('memberHistory:error_generic')))
      .finally(() => setLoading(false))
  }

  const closeModal = () => setShowModal(false)

  const loadMore = () => {
    setLoadingMore(true)
    fetchPage(events.length)
      .then((res) => {
        setEvents((prev) => [...prev, ...(res?.events || [])])
        setHasMore(Boolean(res?.hasMore))
      })
      .catch(() => setError(t('memberHistory:error_generic')))
      .finally(() => setLoadingMore(false))
  }

  // renderTrigger : même mécanisme que AdminEditProfileButton — permet au
  // parent de regrouper ce déclencheur dans un menu déroulant plutôt qu'un
  // CButton autonome. Défaut = CButton (comportement historique).
  const trigger = renderTrigger ? (
    renderTrigger({ onClick: openModal, label: t('memberHistory:button'), icon: cilHistory })
  ) : (
    <CButton
      color="light"
      size="sm"
      onClick={openModal}
      title={t('memberHistory:tooltip')}
      style={{ zIndex: 10, position: 'relative' }}
    >
      <CIcon icon={cilHistory} className="me-1" />
      {t('memberHistory:button')}
    </CButton>
  )

  return (
    <>
      {trigger}

      <CModal visible={showModal} onClose={closeModal} size="lg" scrollable>
        <CModalHeader>
          <CModalTitle>
            {t('memberHistory:modal_title', { name: `${member.firstName} ${member.lastName}` })}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {error && (
            <CAlert color="danger" className="py-2 mb-3" style={{ fontSize: '0.9rem' }}>
              {error}
            </CAlert>
          )}

          {loading ? (
            <div className="d-flex justify-content-center py-4">
              <CSpinner color="primary" />
            </div>
          ) : events.length === 0 && !error ? (
            <p className="text-medium-emphasis mb-0">{t('memberHistory:empty')}</p>
          ) : (
            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              {events.map((event, index) => {
                const { iconKey, title, detail } = formatHistoryEvent(event, t)
                return (
                  <div
                    key={`${event.type}-${event.date}-${index}`}
                    className="d-flex align-items-start gap-2 py-2"
                    style={{
                      borderBottom:
                        index < events.length - 1 ? '1px solid var(--cui-border-color)' : 'none',
                    }}
                  >
                    <CIcon
                      icon={ICONS[iconKey] || cilFlagAlt}
                      className="mt-1 flex-shrink-0 text-medium-emphasis"
                    />
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{title}</div>
                      {detail && <div className="text-medium-emphasis small">{detail}</div>}
                    </div>
                    <div className="text-medium-emphasis small flex-shrink-0">
                      {formatDateI18n(event.date, 'PP', i18n.language)}
                    </div>
                  </div>
                )
              })}

              {hasMore && (
                <div className="text-center pt-3">
                  <CButton
                    color="secondary"
                    variant="outline"
                    size="sm"
                    onClick={loadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <CSpinner size="sm" /> : t('memberHistory:load_more')}
                  </CButton>
                </div>
              )}
            </div>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={closeModal}>
            {t('memberHistory:close')}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default MemberHistoryButton
